/**
 * 「계정」 — 이름·회사명·연락처와 비밀번호·탈퇴 (261013-account-brand-membership). 브랜드와 무관한 계정 단위 설정이다.
 * ★ **이메일·휴대폰을 바꾸면 새 값을 인증해야 저장된다** — 가입과 같은 inline 인증(`InlineVerify`)이고, 서버도 인증 기록을
 *   확인·소비한다. 값이 그대로면 인증을 묻지 않는다. 이메일은 로그인 아이디라 바꾸면 다음 로그인부터 새 주소다.
 * ★ 탈퇴: 혼자 소속된 브랜드는 지워지고, 다른 사람과 함께인데 마지막 소유자이면 서버가 막는다(먼저 소유자를 넘긴다).
 */
import { account, signup } from '@geo-admin/shared/contract/api/index.ts';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { PageTitle } from '../components/PageTitle.tsx';
import { Button } from '../components/ui/button.tsx';
import { Card, CardBody, CardHeader } from '../components/ui/card.tsx';
import { FieldError, Input, Label } from '../components/ui/form.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi, useApiMutation } from '../lib/api.ts';
import { call } from '../lib/call.ts';
import { InlineVerify, normalizeFor } from './signup.tsx';

export function AccountPage() {
  return (
    <>
      <PageTitle title="계정" />
      <div className="grid gap-4 lg:grid-cols-2">
        <ProfileCard />
        <div className="grid content-start gap-4">
          <Card>
            <CardHeader title="비밀번호" />
            <CardBody>
              <Link to="/app/account/password" className="font-semibold text-live hover:underline">
                비밀번호 바꾸기
              </Link>
            </CardBody>
          </Card>
          <WithdrawCard />
        </div>
      </div>
    </>
  );
}

function ProfileCard() {
  const profile = useApi(account.getAccount);
  const update = useApiMutation(account.updateAccount);
  const [form, setForm] = useState({ name: '', companyName: '', email: '', phone: '' });
  const [saved, setSaved] = useState(false);
  /** 인증이 끝난 값 자체를 들고 있는다 — 입력이 바뀌면 자동으로 무효가 된다(가입 화면과 같은 규칙) */
  const [verified, setVerified] = useState<{ phone: string | null; email: string | null }>({
    phone: null,
    email: null,
  });
  // 인증번호 남은 시간 — 칸마다 두지 않고 화면에 하나
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const a = profile.data?.account;
  useEffect(() => {
    if (a) setForm({ name: a.name ?? '', companyName: a.companyName ?? '', email: a.email, phone: a.phone ?? '' });
  }, [a]);

  if (profile.isLoading) return <Loading />;
  if (profile.error) return <ErrorBox error={profile.error} />;
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setSaved(false);
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };

  const changed = (kind: 'email' | 'phone') => {
    const next = normalizeFor(kind, form[kind]);
    const before = normalizeFor(kind, (kind === 'email' ? a?.email : a?.phone) ?? '');
    return next !== '' && next !== before;
  };
  const isVerified = (kind: 'email' | 'phone') =>
    verified[kind] !== null && verified[kind] === normalizeFor(kind, form[kind]);
  const needsEmail = changed('email') && !isVerified('email');
  const needsPhone = changed('phone') && !isVerified('phone');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (needsEmail || needsPhone) return;
    update.mutate(
      {
        body: {
          ...(form.name.trim() ? { name: form.name.trim() } : {}),
          companyName: form.companyName.trim() || null,
          ...(changed('email') ? { email: form.email.trim() } : {}),
          ...(changed('phone') ? { phone: form.phone.trim() } : {}),
        },
      },
      {
        onSuccess: () => {
          setSaved(true);
          setVerified({ phone: null, email: null });
        },
      },
    );
  };
  return (
    <Card>
      <CardHeader title="내 정보" description="회사명은 운영팀이 누구인지 알아보는 데 씁니다" />
      <CardBody>
        <form className="grid gap-3" onSubmit={submit}>
          <Label>
            이름
            <Input value={form.name} onChange={set('name')} maxLength={60} />
          </Label>
          <Label>
            회사명 (선택)
            <Input value={form.companyName} onChange={set('companyName')} maxLength={120} />
          </Label>
          <div>
            <Label>
              이메일 (로그인 아이디)
              <Input type="email" value={form.email} onChange={set('email')} />
            </Label>
            {changed('email') ? (
              <InlineVerify
                kind="email"
                target={form.email}
                now={now}
                verified={isVerified('email')}
                onVerified={(v) => setVerified((s) => ({ ...s, email: v }))}
              />
            ) : null}
          </div>
          <div>
            <Label>
              휴대폰
              <Input type="tel" value={form.phone} onChange={set('phone')} placeholder="010-1234-5678" />
            </Label>
            {changed('phone') ? (
              <InlineVerify
                kind="phone"
                target={form.phone}
                now={now}
                verified={isVerified('phone')}
                onVerified={(v) => setVerified((s) => ({ ...s, phone: v }))}
              />
            ) : null}
          </div>
          <FieldError>{update.error?.message}</FieldError>
          <div className="flex items-center justify-end gap-3">
            {needsEmail || needsPhone ? (
              <span className="text-slate-500 text-sm">바꾼 이메일·휴대폰을 인증하면 저장할 수 있습니다</span>
            ) : saved ? (
              <span className="text-slate-500 text-sm">저장했습니다</span>
            ) : null}
            <Button type="submit" disabled={update.isPending || needsEmail || needsPhone}>
              저장
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

function WithdrawCard() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const leave = async () => {
    setError(null);
    setBusy(true);
    try {
      await call(signup.withdraw, { body: { password } });
      qc.clear();
      await navigate({ to: '/login' });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card>
      <CardHeader title="탈퇴" />
      <CardBody className="grid gap-3 text-sm">
        <p className="text-slate-600">
          혼자 관리하는 브랜드는 측정이 멈추고 지워집니다. 다른 사람과 함께 관리하는 브랜드의 마지막 소유자라면 먼저
          「브랜드」에서 소유자를 넘겨 주세요.
        </p>
        {open ? (
          <>
            <Label>
              비밀번호
              <Input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Label>
            <FieldError>{error}</FieldError>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>
                취소
              </Button>
              <Button variant="danger" disabled={busy || !password} onClick={() => void leave()}>
                탈퇴
              </Button>
            </div>
          </>
        ) : (
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setOpen(true)}>
              탈퇴하기
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
